/**
 * Agent Chat Drawer State Management
 * Centralized localStorage handling for drawer state
 */

const DRAWER_STATE_KEY = 'agentChatDrawerState'

export interface DrawerState {
  isOpen: boolean
  width: number
  sessionId: string | null // Current active session (null = show list)
}

const DEFAULT_STATE: DrawerState = {
  isOpen: false,
  width: 448,
  sessionId: null,
}

/**
 * Get drawer state from localStorage
 */
export const getDrawerState = (): DrawerState => {
  try {
    const saved = localStorage.getItem(DRAWER_STATE_KEY)
    if (saved) {
      const parsed = JSON.parse(saved) as Partial<DrawerState>
      return {
        isOpen: parsed.isOpen ?? DEFAULT_STATE.isOpen,
        width: parsed.width ?? DEFAULT_STATE.width,
        sessionId: parsed.sessionId ?? DEFAULT_STATE.sessionId,
      }
    }
  } catch (error) {
    console.error('[DrawerState] Failed to parse drawer state:', error)
  }
  return { ...DEFAULT_STATE }
}

/**
 * Save drawer state to localStorage
 */
export const saveDrawerState = (state: Partial<DrawerState>): void => {
  try {
    const currentState = getDrawerState()
    const newState = { ...currentState, ...state }
    localStorage.setItem(DRAWER_STATE_KEY, JSON.stringify(newState))
  } catch (error) {
    console.error('[DrawerState] Failed to save drawer state:', error)
  }
}

/**
 * Update only the isOpen state
 */
export const setDrawerOpen = (isOpen: boolean): void => {
  saveDrawerState({ isOpen })
}

/**
 * Update only the width state
 */
export const setDrawerWidth = (width: number): void => {
  saveDrawerState({ width })
}

/**
 * Update only the sessionId state
 * Use null to show chat list, or provide sessionId to open specific chat
 */
export const setStoredSessionId = (sessionId: string | null): void => {
  saveDrawerState({ sessionId })
}

/**
 * Get current session ID from drawer state
 */
export const getStoredSessionId = (): string | null => {
  return getDrawerState().sessionId
}
